import {
  Key,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { TransitionStage } from './constants'
import { useEvent } from 'hooks/useEvent'

interface TransitionItem<T> {
  key: Key
  timeout: number
  asyncInit?: boolean
  initTimeout?: number
  asyncLeave?: boolean
  leavePrepareTimeout?: number
  render(
    stage: TransitionStage,
    visible: boolean,
    onTransitionEnd: () => void,
    data: T | undefined,
    onInit: () => void,
    onLeaveReady: () => void,
  ): ReactNode
}

interface TransitionItemState<T> extends TransitionItem<T> {
  state: TransitionStage
  deadLine: number
  initDeadline: number
  leaveReady: boolean
  leavePrepareDeadline: number
}

const DEFAULT_INIT_TIMEOUT = 2000
const DEFAULT_LEAVE_PREPARE_TIMEOUT = 2000

const isUnfinishedInitTask = function <T = unknown>(i: TransitionItemState<T>) {
  return i.state === TransitionStage.preInit
}

const hasUnfinishedInitTask = function <T = unknown>(
  states: TransitionItemState<T>[],
) {
  return states.find((i) => isUnfinishedInitTask(i))
}

const isUnfinishedLeavePrepare = function <T = unknown>(
  i: TransitionItemState<T>,
) {
  return i.state === TransitionStage.preLeave && !i.leaveReady
}

const hasUnfinishedLeavePrepareTask = function <T = unknown>(
  states: TransitionItemState<T>[],
) {
  return states.find((i) => isUnfinishedLeavePrepare(i))
}

function createNewStates<T = unknown>(
  states: TransitionItemState<T>[],
  oldItems: TransitionItem<T>[] | null,
  items: TransitionItem<T>[],
  synchronizedEnterExit: boolean,
) {
  const oldKeys = new Set(states.map((i) => i.key))
  const newKeys = new Set(items.map((i) => i.key))

  const duplicatedStateKeys = states
    .filter((s) => newKeys.has(s.key))
    .map((i) => i.key)
  const duplicatedItemKeys = items
    .filter((i) => oldKeys.has(i.key))
    .map((i) => i.key)

  const sharedKeys = duplicatedItemKeys.filter(
    (ik, index) => ik === duplicatedStateKeys[index],
  )

  const splitWithKey = function <T1 extends TransitionItem<T>>(
    items: T1[],
    anchors: Key[],
  ): { anchor: T1 | null; items: T1[] }[] {
    const segments: { anchor: T1 | null; items: T1[] }[] = [
      { anchor: null, items: [] },
    ]
    for (const item of items) {
      if (anchors.includes(item.key)) {
        segments.push({
          anchor: item,
          items: [],
        })
      } else {
        const currentSeg = segments[segments.length - 1]
        currentSeg.items.push(item)
      }
    }
    return segments
  }

  const prev = splitWithKey(states, sharedKeys)
  const next = splitWithKey(items, sharedKeys)

  let res: TransitionItemState<T>[] = []

  const now = Date.now()

  for (let i = 0; i < next.length; i++) {
    const prevSeg = prev[i]
    const nextSeg = next[i]

    const toRemove = new Set(
      prevSeg.items.filter((i) => !newKeys.has(i.key)).map((i) => i.key),
    )
    const movedOut = new Set(
      prevSeg.items.filter((i) => newKeys.has(i.key)).map((i) => i.key),
    )

    const merged = [
      ...prevSeg.items.map((i) => i.key),
      ...nextSeg.items.map((i) => i.key),
    ]
      .filter((i) => !movedOut.has(i))
      .map((key): TransitionItemState<T> => {
        const stateItem = states.find((s) => s.key === key)
        if (stateItem) {
          if (toRemove.has(stateItem.key)) {
            if (stateItem.state !== TransitionStage.leave) {
              if (stateItem.state === TransitionStage.preInit) {
                return {
                  ...stateItem,
                  state: TransitionStage.__internal__preInitDirectRemove,
                  deadLine: 0,
                }
              }
              if (stateItem.state === TransitionStage.preLeave) {
                return stateItem
              }
              return {
                ...stateItem,
                state: TransitionStage.preLeave,
                leavePrepareDeadline: stateItem.asyncLeave
                  ? stateItem.leavePrepareDeadline ||
                    Date.now() +
                      (stateItem.leavePrepareTimeout ??
                        DEFAULT_LEAVE_PREPARE_TIMEOUT)
                  : 0,
              }
            } else {
              return stateItem
            }
          } else {
            return stateItem
          }
        } else {
          const newItem = items.find((i) => i.key === key)!
          return {
            ...newItem,
            initDeadline:
              newItem.asyncInit ?? false
                ? Date.now() + (newItem.initTimeout ?? DEFAULT_INIT_TIMEOUT)
                : Date.now(),
            state: TransitionStage.preInit,
            deadLine: 0,
            leaveReady: !newItem.asyncLeave,
            leavePrepareDeadline: 0,
          }
        }
      })

    if (prevSeg.anchor) {
      res.push(prevSeg.anchor)
    }

    res.push(
      ...merged.filter(
        (i) => i.state !== TransitionStage.__internal__preInitDirectRemove,
      ),
    )
  }

  if (
    synchronizedEnterExit &&
    (hasUnfinishedInitTask(res) || hasUnfinishedLeavePrepareTask(res))
  ) {
    // keep pre-leave state because we can't do transition yet
  } else {
    res = res.map((i) => {
      if (i.state === TransitionStage.preLeave && i.leaveReady) {
        return {
          ...i,
          state: TransitionStage.leave,
          deadLine: now + i.timeout,
        }
      }
      return i
    })
  }

  return res.map((s) => {
    const prop = items.find((i) => i.key === s.key)
    if (prop) {
      return {
        ...s,
        render: prop.render,
      }
    }
    const oldProp = oldItems?.find((i) => i.key === s.key)
    if (oldProp) {
      return {
        ...s,
        render: oldProp.render,
      }
    }
    return s
  })
}

export interface TransitionListProps<T> {
  items: TransitionItem<T>[]
  data?: T
  synchronizedEnterExit?: boolean
}

const TransitionList = function TransitionList<T = unknown>({
  items,
  data,
  synchronizedEnterExit = false,
}: TransitionListProps<T>) {
  const [states, setStates] = useState<TransitionItemState<T>[]>([])
  const [oldItems, setOldItems] = useState<TransitionItem<T>[] | null>(null)

  const newStates = createNewStates(
    states,
    oldItems,
    items,
    synchronizedEnterExit,
  )

  if (JSON.stringify(newStates) !== JSON.stringify(states)) {
    setOldItems(items)
    setStates(newStates)
  }

  // handle 'init state'
  useLayoutEffect(() => {
    if (!states.find((i) => i.state === TransitionStage.init)) return
    if (hasUnfinishedInitTask(states)) {
      return
    }
    if (
      synchronizedEnterExit &&
      (hasUnfinishedLeavePrepareTask(states) || hasUnfinishedInitTask(states))
    ) {
      return
    }

    const id = requestAnimationFrame(() => {
      const now = Date.now()
      const newStates: TransitionItemState<T>[] = states.map((i) => {
        if (i.state === TransitionStage.init) {
          return {
            ...i,
            state: TransitionStage.enter,
            deadLine: i.timeout + now,
          }
        }
        return i
      })
      setStates(newStates)
    })

    return () => {
      cancelAnimationFrame(id)
    }
  }, [items, states, synchronizedEnterExit])

  const finishInit = useCallback(
    (k: Key) => {
      setStates((states) => {
        const item = states.find(
          (i) => isUnfinishedInitTask(i) && i.key === k,
        )

        if (item != null) {
          const newStates = states.map((s) => {
            if (s.key === k) {
              return {
                ...s,
                state: TransitionStage.init,
                initDeadline: 0,
              }
            }
            return s
          })
          if (hasUnfinishedInitTask(newStates)) {
            return newStates
          } else {
            return createNewStates(
              newStates,
              items,
              items,
              synchronizedEnterExit,
            )
          }
        }
        return states
      })
    },
    [items, synchronizedEnterExit],
  )

  // handles async init timeout
  useLayoutEffect(() => {
    const preparingItems = states.filter(
      (i) => i.state === TransitionStage.preInit,
    )

    const now = Date.now()

    const closest = preparingItems.reduce(
      (p, c) => (c.initDeadline < p.d ? { i: c, d: c.initDeadline } : p),
      { i: null as null | TransitionItemState<T>, d: Infinity },
    )

    if (closest.i != null) {
      const id = setTimeout(() => {
        finishInit(closest.i!.key)
      }, closest.i.initDeadline - now)
      return () => {
        clearTimeout(id)
      }
    }
  }, [finishInit, states])

  const finishLeavePrepare = useCallback(
    (k: Key) => {
      setStates((states) => {
        const item = states.find(
          (i) => isUnfinishedLeavePrepare(i) && i.key === k,
        )

        if (item != null) {
          const newStates = states.map((s) => {
            if (s.key === k) {
              return {
                ...s,
                state: TransitionStage.preLeave,
                deadLine: 0,
                leaveReady: true,
                leavePrepareDeadline: 0,
              }
            }
            return s
          })
          if (
            synchronizedEnterExit &&
            (hasUnfinishedLeavePrepareTask(newStates) ||
              hasUnfinishedInitTask(newStates))
          ) {
            return newStates
          } else {
            return createNewStates(
              newStates,
              items,
              items,
              synchronizedEnterExit,
            )
          }
        }

        return states
      })
    },
    [items, synchronizedEnterExit],
  )

  // handles leave prepare timeout
  useLayoutEffect(() => {
    const preparingItems = states.filter(
      (i) => i.state === TransitionStage.preLeave && !i.leaveReady,
    )

    const now = Date.now()

    const closest = preparingItems.reduce(
      (p, c) =>
        c.leavePrepareDeadline < p.d
          ? { i: c, d: c.leavePrepareDeadline }
          : p,
      { i: null as null | TransitionItemState<T>, d: Infinity },
    )

    if (closest.i != null) {
      const id = setTimeout(() => {
        finishLeavePrepare(closest.i!.key)
      }, closest.i.leavePrepareDeadline - now)
      return () => {
        clearTimeout(id)
      }
    }
  }, [finishLeavePrepare, states])

  const finishEnter = useCallback(
    (k: Key) => {
      if (
        states.find(
          (s) => s.key === k && s.state === TransitionStage.enter,
        )
      ) {
        setStates((states) =>
          states.find(
            (i) => i.key === k && i.state === TransitionStage.enter,
          ) != null
            ? states.map((i) => {
                if (i.key === k) {
                  return {
                    ...i,
                    state: TransitionStage.normal,
                    deadLine: 0,
                  }
                } else {
                  return i
                }
              })
            : states,
        )
      }
    },
    [states],
  )

  // handles timeout
  useLayoutEffect(() => {
    const animatingItems = states.filter(
      (i) =>
        i.state === TransitionStage.enter ||
        i.state === TransitionStage.leave,
    )

    const now = Date.now()

    const closest = animatingItems.reduce(
      (p, c) => (c.deadLine < p.d ? { i: c, d: c.deadLine } : p),
      { i: null as null | TransitionItemState<T>, d: Infinity },
    )

    if (closest.i != null) {
      const timeoutTarget = closest.d
      const duration = timeoutTarget - now

      const onTimeout = () => {
        console.error('[stage] animation timeout')
        setStates((states) =>
          states
            .map((i): TransitionItemState<T> => {
              if (
                i.state === TransitionStage.enter &&
                i.deadLine <= timeoutTarget
              ) {
                return {
                  ...i,
                  state: TransitionStage.normal,
                }
              } else {
                return i
              }
            })
            .filter((i) => {
              if (
                i.state === TransitionStage.leave &&
                i.deadLine <= timeoutTarget
              ) {
                return false
              } else {
                return true
              }
            }),
        )
      }

      if (duration <= 0) {
        onTimeout()
      } else {
        const id = setTimeout(onTimeout, duration)
        return () => {
          clearTimeout(id)
        }
      }
    }
  }, [states])

  const finishLeave = useCallback((k: Key) => {
    setStates((states) =>
      states.find((i) => i.key === k) != null
        ? states.filter((i) => i.key !== k)
        : states,
    )
  }, [])

  const endTransition = useCallback(
    (key: Key) => {
      const item = states.find((i) => i.key === key)
      if (item) {
        if (item.state === 'enter') {
          finishEnter(key)
        }
        if (item.state === 'leave') {
          finishLeave(key)
        }
      }
    },
    [finishEnter, finishLeave, states],
  )

  const mappedState = useMemo(() => {
    return states.map((s) => {
      const matchingProp = items.find((i) => i.key === s.key)
      if (matchingProp != null) {
        return {
          ...s,
          render: matchingProp.render,
        }
      } else {
        return s
      }
    })
  }, [items, states])

  const finishInitEvent = useEvent(finishInit)
  const finishLeavePrepareEvent = useEvent(finishLeavePrepare)
  const endTransitionEvent = useEvent(endTransition)

  const resJSX = useMemo(() => {
    return (
      <>
        {mappedState.map((i) => {
          const visible = i.state === 'enter' || i.state === 'normal'
          return i.render(
            i.state,
            visible,
            () => endTransitionEvent(i.key),
            data,
            () => finishInitEvent(i.key),
            () => finishLeavePrepareEvent(i.key),
          )
        })}
      </>
    )
  }, [
    data,
    endTransitionEvent,
    finishInitEvent,
    finishLeavePrepareEvent,
    mappedState,
  ])

  return resJSX
}

export default TransitionList
